if (!customElements.get("ashpveda-sticky-cta")) {
  customElements.define(
    "ashpveda-sticky-cta",
    class AshpvedaStickyCTA extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;

        this.addButton = this.querySelector("[data-sticky-submit]");

        this.buyButton = this.querySelector("[data-sticky-buy-now]");

        this.addButtonText = this.querySelector("[data-sticky-button-text]");

        this.buyButtonText = this.querySelector("[data-sticky-buy-text]");

        this.addSpinner = this.addButton?.querySelector(
          ".loading-overlay__spinner",
        );

        this.buySpinner = this.buyButton?.querySelector(
          ".loading-overlay__spinner",
        );

        this.stickyPrice = this.querySelector("[data-sticky-price]");

        this.sectionRoot =
          this.closest(`[data-section-id="${this.sectionId}"]`) ||
          document.querySelector(`[data-section-id="${this.sectionId}"]`);

        if (
          !this.sectionId ||
          !this.addButton ||
          !this.buyButton ||
          !this.sectionRoot
        ) {
          return;
        }

        this.handleAddToCart = this.handleAddToCart.bind(this);

        this.handleBuyNow = this.handleBuyNow.bind(this);

        this.handleViewportChange = this.handleViewportChange.bind(this);

        this.updateStickyHeight = this.updateStickyHeight.bind(this);

        this.addButton.addEventListener("click", this.handleAddToCart);

        this.buyButton.addEventListener("click", this.handleBuyNow);

        this.mobileQuery = window.matchMedia("(max-width: 749px)");

        if (this.mobileQuery.addEventListener) {
          this.mobileQuery.addEventListener(
            "change",
            this.handleViewportChange,
          );
        } else {
          this.mobileQuery.addListener(this.handleViewportChange);
        }

        /*
         * Update sticky height automatically, so the fixed
         * bar never overlaps the end of the page.
         */
        this.resizeObserver = new ResizeObserver(this.updateStickyHeight);

        this.resizeObserver.observe(this);

        /*
         * Watch original product information for variant,
         * price, availability and loading-state changes.
         */
        this.mutationObserver = new MutationObserver((mutations) => {
          const onlyStickyMutations = mutations.every((mutation) =>
            this.contains(mutation.target),
          );

          if (onlyStickyMutations) return;

          this.scheduleSync();
        });

        this.mutationObserver.observe(this.sectionRoot, {
          attributes: true,
          childList: true,
          subtree: true,
          characterData: true,
          attributeFilter: ["class", "disabled", "aria-disabled", "value"],
        });

        this.handleViewportChange();
        this.sync();
      }

      disconnectedCallback() {
        this.addButton?.removeEventListener("click", this.handleAddToCart);

        this.buyButton?.removeEventListener("click", this.handleBuyNow);

        this.mutationObserver?.disconnect();
        this.resizeObserver?.disconnect();

        if (this.mobileQuery?.removeEventListener) {
          this.mobileQuery.removeEventListener(
            "change",
            this.handleViewportChange,
          );
        } else {
          this.mobileQuery?.removeListener(this.handleViewportChange);
        }

        document.documentElement.classList.remove("ashpveda-sticky-cta-active");

        document.documentElement.style.removeProperty(
          "--ashpveda-sticky-cta-height",
        );
      }

      get originalButton() {
        return document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      }

      get originalForm() {
        return (
          this.originalButton?.form ||
          document.getElementById(`product-form-${this.sectionId}`)
        );
      }

      get originalPrice() {
        return document.getElementById(`price-${this.sectionId}`);
      }

      scheduleSync() {
        if (this.syncFrame) {
          cancelAnimationFrame(this.syncFrame);
        }

        this.syncFrame = requestAnimationFrame(() => {
          this.sync();
        });
      }

      handleViewportChange() {
        const isMobile = this.mobileQuery.matches;

        document.documentElement.classList.toggle(
          "ashpveda-sticky-cta-active",
          isMobile,
        );

        if (isMobile) {
          requestAnimationFrame(this.updateStickyHeight);
        } else {
          document.documentElement.style.setProperty(
            "--ashpveda-sticky-cta-height",
            "0px",
          );
        }
      }

      updateStickyHeight() {
        if (!this.mobileQuery.matches) return;

        const height = Math.ceil(this.getBoundingClientRect().height);

        document.documentElement.style.setProperty(
          "--ashpveda-sticky-cta-height",
          `${height}px`,
        );
      }

      handleAddToCart() {
        const originalButton = this.originalButton;

        if (
          !originalButton ||
          originalButton.disabled ||
          originalButton.getAttribute("aria-disabled") === "true"
        ) {
          return;
        }

        /*
         * Use the original product Add to Cart button.
         * The theme continues handling:
         * - variant
         * - quantity
         * - cart drawer
         * - errors
         */
        originalButton.click();
      }

      async handleBuyNow() {
        const originalButton = this.originalButton;
        const originalForm = this.originalForm;

        if (
          !originalButton ||
          !originalForm ||
          originalButton.disabled ||
          originalButton.getAttribute("aria-disabled") === "true" ||
          this.buyButton.classList.contains("loading")
        ) {
          return;
        }

        this.setBuyLoading(true);

        try {
          const formData = new FormData(originalForm);

          /*
           * These fields are used only for cart-drawer
           * section rendering and are unnecessary for checkout.
           */
          formData.delete("sections");
          formData.delete("sections_url");

          const response = await fetch(
            window.routes?.cart_add_url || "/cart/add.js",
            {
              method: "POST",
              headers: {
                Accept: "application/json",
                "X-Requested-With": "XMLHttpRequest",
              },
              body: formData,
            },
          );

          const result = await response.json();

          if (!response.ok || result.status || result.errors) {
            throw new Error(
              result.description ||
                result.message ||
                "Unable to add this product.",
            );
          }

          const root = window.Shopify?.routes?.root || "/";

          window.location.assign(`${root}checkout`);
        } catch (error) {
          console.error("Sticky Buy Now error:", error);

          const productForm = originalButton.closest("product-form");

          if (
            productForm &&
            typeof productForm.handleErrorMessage === "function"
          ) {
            productForm.handleErrorMessage(error.message);
          } else {
            alert(error.message || "Something went wrong. Please try again.");
          }

          this.setBuyLoading(false);
        }
      }

      setBuyLoading(isLoading) {
        this.buyButton.classList.toggle("loading", isLoading);

        this.buyButton.disabled = isLoading;

        this.buySpinner?.classList.toggle("hidden", !isLoading);

        if (this.buyButtonText) {
          this.buyButtonText.textContent = isLoading
            ? "Processing..."
            : "Buy now";
        }
      }

      sync() {
        this.syncPrice();
        this.syncButtons();

        requestAnimationFrame(this.updateStickyHeight);
      }

      syncPrice() {
        const originalPrice = this.originalPrice;

        if (!originalPrice || !this.stickyPrice) {
          return;
        }

        if (this.stickyPrice.innerHTML !== originalPrice.innerHTML) {
          this.stickyPrice.innerHTML = originalPrice.innerHTML;
        }
      }

      syncButtons() {
        const originalButton = this.originalButton;

        if (!originalButton) return;

        const isLoading = originalButton.classList.contains("loading");

        const isDisabled =
          originalButton.disabled ||
          originalButton.getAttribute("aria-disabled") === "true";

        const originalText =
          originalButton.querySelector("span")?.textContent?.trim() || "";

        this.addButton.disabled = isDisabled || isLoading;

        this.buyButton.disabled =
          isDisabled ||
          isLoading ||
          this.buyButton.classList.contains("loading");

        this.addButton.classList.toggle("loading", isLoading);

        this.addSpinner?.classList.toggle("hidden", !isLoading);

        if (this.addButtonText) {
          if (isLoading) {
            this.addButtonText.textContent = "Adding...";
          } else if (isDisabled) {
            this.addButtonText.textContent = originalText || "Sold out";
          } else {
            this.addButtonText.textContent = originalText || "Add to cart";
          }
        }

        if (
          this.buyButtonText &&
          !this.buyButton.classList.contains("loading")
        ) {
          this.buyButtonText.textContent = isDisabled ? "Sold out" : "Buy now";
        }
      }
    },
  );
}
